import WarpElement from '@warp-ds/elements-core';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@warp-ds/elements/components/button';
import { i18n } from '@lingui/core';
import { Phone, ProfileType } from '../../server/types.js';
import { trackPhone, trackShowPhone } from '../utils/pulseTrackingUtils.js';

const containerId = 'fixed-actions-container';
type VisibleState = 'visible' | 'initial' | 'hidden';

@customElement('fixed-actions')
export class FixedActions extends WarpElement {
	@property({ type: String }) adId!: string;
	@property({ type: String }) orgName!: string;
	@property({ type: String }) contactUsUrl?: string;
	@property({ type: String }) externalItemLink?: string;
	@property({ type: Array }) phone?: Phone[];

	@property({ type: ProfileType }) profileType?: ProfileType;

	static styles = [
		...WarpElement.styles,
		css`
			@media (width >= 768px) {
				.animate-initial {
					animation: disappear 0s forwards;
				}

				.animate-visible {
					animation: appear 0.15s ease-in-out forwards;
				}

				.animate-hidden {
					animation: disappear 0.15s ease-in-out forwards;
				}

				@keyframes appear {
					0% {
						transform: translateY(-100%);
						opacity: 1;
					}
					100% {
						transform: translateY(0);
						opacity: 1;
					}
				}

				@keyframes disappear {
					0% {
						transform: translateY(0);
					}
					99% {
						transform: translateY(-100%);
						opacity: 1;
					}
					100% {
						transform: translateY(-100%);
						opacity: 0;
					}
				}
			}

			@css-placeholder-tGsRJR;
		`,
	];

	@state()
	private phoneHidden = true;

	@state()
	private state: VisibleState = 'initial';

	@state()
	private focused: boolean = false;

	focusHandler = () => {
		this.focused = true;
	};

	blurHandler = () => {
		this.focused = false;
	};

	isBigScreen = () => {
		return window.innerWidth >= 768;
	};

	changeState = (visible: boolean) => {
		let newState = this.state;
		if (this.state !== 'initial' || visible) {
			newState = visible ? 'visible' : 'hidden';
		}

		this.adjustScrollPadding(newState);
		this.state = newState;
	};

	getHeight = () => {
		return (
			this.shadowRoot?.getElementById(containerId)?.getBoundingClientRect()
				.height ?? 0
		);
	};

	adjustScrollPadding = (newState: VisibleState) => {
		if (this.state !== 'visible' && newState === 'visible') {
			const height = this.getHeight();
			const currentPadding =
				document.documentElement.style.getPropertyValue('scroll-padding-top') ||
				'0';
			document.documentElement.style.setProperty(
				'scroll-padding-top',
				`${parseInt(currentPadding) + height}px`,
			);
		}

		if (this.state !== 'hidden' && newState === 'hidden') {
			const height =
				this.shadowRoot?.getElementById(containerId)?.getBoundingClientRect()
					.height ?? 0;

			const currentPadding =
				document.documentElement.style.getPropertyValue('scroll-padding-top');
			document.documentElement.style.setProperty(
				'scroll-padding-top',
				`${parseInt(currentPadding) - height}px`,
			);
		}
	};

	listenerHandler = (event: Event) => {
		const detail = (event as CustomEvent<{ visible: boolean }>).detail;
		if (this.isBigScreen()) {
			this.changeState(detail.visible);
		}
	};

	connectedCallback(): void {
		super.connectedCallback();

		window.addEventListener('action-visibility-change', this.listenerHandler);
		this.addEventListener('focus', this.focusHandler);
		this.addEventListener('blur', this.blurHandler);
		if (!this.isBigScreen()) {
			document.body.style.setProperty(
				'padding-bottom',
				`${this.getHeight()}px`,
			);
		}
	}

	disconnectedCallback(): void {
		document.removeEventListener(
			'action-visibility-change',
			this.listenerHandler,
		);
		this.removeEventListener('focus', this.focusHandler);
		this.removeEventListener('blur', this.blurHandler);
	}

	phoneNumber = () => {
		return this.phoneHidden
			? html`
					<w-button
						class="flex flex-1 md:flex-none"
						full-width
						variant=${this.contactUsUrl || this.externalItemLink
							? 'secondary'
							: 'primary'}
						@click="${() => {
							trackShowPhone(this.orgName);
							this.phoneHidden = false;
						}}"
					>
						${i18n.t({
							id: 'show.phone.number',
							message: 'Show phone number',
						})}
					</w-button>
				`
			: html`
					<a
						class="flex-1 md:flex-none"
						href="${this.phone?.[0].phoneAsLink}"
						@click="${() => trackPhone(this.adId, this.orgName)}"
						@focus=${this.focusHandler}
					>
						${this.phone?.[0]?.phoneFormatted}
					</a>
				`;
	};

	render() {
		return html`
			<div
				data-test-id="action-group"
				class="flex flex-col space-y-16 max-w-[400]"
			>
				<div
					id=${containerId}
					class="fixed z-10 bottom-0 md:bottom-a md:top-[49] left-0 right-0 s-bg animate-${this
						.focused
						? 'visible'
						: this.state}"
					style="box-shadow: 0px 6px 12px 0px rgba(0, 0, 0, 0.20), 0px 10px 20px 0px rgba(0, 0, 0, 0.10);"
				>
					<div
						class="page-container flex gap-16 items-center justify-between py-8!"
					>
						<p class="sr-only md:not-sr-only" aria-hidden="true">
							${i18n.t({
								id: 'sold.by',
								message: 'Sold by',
							})}${' '}
							<b>${this.orgName}</b>
						</p>
						<div
							class="grid grid-cols-2 gap-16 items-center justify-stretch md:justify-start w-full md:w-auto"
						>
							${this.contactUsUrl
								? html`
										<contact-button
											class="flex-1 md:flex-none"
											.contactUsUrl=${this.contactUsUrl}
											.profileType=${this.profileType}
										></contact-button>
									`
								: nothing}
							${this.externalItemLink
								? html`
										<external-item-link
											class="flex-1 md:flex-none"
											.orgName=${this.orgName}
											.externalItemLink=${this.externalItemLink}
											.variant=${this.contactUsUrl ? 'secondary' : 'primary'}
										></external-item-link>
									`
								: nothing}
							${(!this.contactUsUrl || !this.externalItemLink) &&
							this.phone?.length
								? this.phoneNumber()
								: nothing}
						</div>
					</div>
				</div>
			</div>
		`;
	}
}
